import { VIZ_CATEGORICAL } from '@splunk/visualization-color-palettes';
import {
    COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
    LAYOUT_TYPE,
    enhanceOptionsSchemaWithDynamicOptions,
    getPattern,
} from '../utils';

export const pieOptionsSchema = enhanceOptionsSchemaWithDynamicOptions({
    backgroundColor: {
        default: '> themes.defaultBackgroundColor',
        description:
            'Specify the color for the background. You may use a dataSource to apply the color. The default for enterprise light is "#ffffff". The default for enterprise dark is "#000000". The default for prisma dark is "#0b0c0e".',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    collapseExcludeZero: {
        default: false,
        description: 'Specify whether to exclude zeros and non-numeric values in the consolidated slice.',
        type: 'boolean',
    },
    collapseLabel: {
        description: 'Specify the label for the consolidated slice.',
        type: 'string',
        default: 'other',
    },
    legendDisplay: {
        default: 'off',
        description: 'Specify the location of the legend on the panel.',
        pattern: getPattern(['right', 'left', 'top', 'bottom', 'off']),
        type: 'string',
    },
    collapseThreshold: {
        description:
            'Specify the size threshold as a number between 0 and 1 (inclusive), of the whole pie at which slices collapse into one consolidated slice.',
        type: 'number',
        default: 0.01,
    },
    label: {
        description: 'List of string values to display the pie chart labels.',
        type: 'array',
        items: {
            type: 'string',
        },
        default: '> primary | seriesByIndex(0)',
    },
    labelField: {
        default: '> label | getField()',
        description: 'Specify the field that corresponds to the labels.',
        type: 'string',
    },
    labelDisplay: {
        description: 'Specify whether to display the labels and/or slice percentages.',
        type: 'string',
        pattern: getPattern(['values', 'valuesAndPercentage', 'off']),
        default: 'values',
    },
    percentagePrecision: {
        description:
            'Specify the number of decimal places to display for percentages. The minimum value is 0. The maximum value is 16.',
        type: 'number',
        minimum: 0,
        maximum: 16,
        default: 0,
    },
    resultLimit: {
        default: 50000,
        description: 'Specify the number of data points rendered in a chart.',
        type: 'number',
    },
    seriesColors: {
        default: VIZ_CATEGORICAL,
        description: 'Specify the colors used for a series. For example, ["#FF0000", "#0000FF", "#008000"].',
        type: 'array',
        items: {
            type: 'string',
        },
    },
    seriesColorsByField: {
        description:
            'Specify the colors used for specific pie slice labels. For example: {"April": "#008000", "May": "#FFA500"}.',
        type: 'object',
    },
    showDonutHole: {
        default: false,
        description: 'Specify whether the pie should be a donut.',
        type: 'boolean',
    },
    splitByLayout: {
        default: 'off',
        description:
            'Specify the layout method by which to display the visualization, which splits the data into individual visualizations based on a certain category.',
        type: 'string',
        pattern: getPattern([LAYOUT_TYPE.Off, LAYOUT_TYPE.Trellis]),
        enum: [LAYOUT_TYPE.Off, LAYOUT_TYPE.Trellis],
    },
    trellisBackgroundColor: {
        default: '> themes.defaultBackgroundColor',
        description:
            'Specify the color used for the trellis container background by using a hexadecimal code. For example, #0000FF.',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    trellisColumns: {
        description:
            'Specify the number of visualizations to display in a given row of the trellis container. The remaining visualizations will wrap accordingly. If nothing is specified, it will be auto-set based on the trellisMinColumnWidth. The minimum value is 1.',
        type: 'number',
        minimum: 1,
        default: 3,
    },
    trellisMinColumnWidth: {
        default: 250,
        minimum: 1,
        description:
            'Specify the minimum width, in pixels, of each visualization in the trellis container. If the window or panel is resized, the remaining visualizations may be viewed by scrolling. The minimum value is 1.',
        type: 'number',
    },
    trellisPageCount: {
        default: 20,
        minimum: 1,
        description:
            'Specify the maximum number of visualizations to display in a single page in the trellis container. The remaining visualizations will paginate accordingly. The minimum value is 1.',
        type: 'number',
    },
    trellisRowHeight: {
        default: 180,
        minimum: 1,
        description:
            'Specify the height, in pixels, of each visualization in the trellis container. If the window or panel is resized, the remaining visualizations may be viewed by scrolling. The minimum value is 1.',
        type: 'number',
    },
    trellisSortBy: {
        default: 'result',
        description:
            'Specify the sort method for the individual visualizations in the trellis container. Use result to sort by search result order, or name to sort alphabetically by visualization label.',
        type: 'string',
        pattern: getPattern(['result', 'name']),
        enum: ['result', 'name'],
    },
    trellisSortOrder: {
        default: 'ascending',
        description:
            'Specify the sort order for the individual visualizations in the trellis container. This setting applies to the sort method specified in trellisSortBy.',
        type: 'string',
        pattern: getPattern(['ascending', 'descending']),
        enum: ['ascending', 'descending'],
    },
    trellisSplitBy: {
        description:
            'Specify the field name of the column with categories used, or “aggregations”, to split the data into individual visualizations for trellis display, if applicable. If a SPL `timechart` command is used, this may default to the SPL `by` clause field. If a SPL `chart` command is used, this may default to the first string field.',
        type: 'string',
    },
    value: {
        description: 'List of numerical values to power the pie chart.',
        type: 'array',
        items: {
            type: 'number',
        },
        default: '> primary | seriesByIndex(1)',
    },
    valueField: {
        default: '> value | getField()',
        description: 'Specify the field that corresponds to the data powering values.',
        type: 'string',
    },
});
